import { useMutation, useQuery } from 'react-query';

import type {
  CreateRoomBody,
  SendMessageBody,
  UploadFileBody,
} from '~/api/data-source/chatting';
import { CHATTING_QUERY_KEY } from '~/api/query-keys/chatting';
import { chattingRepository } from '~/api/repository/chatting';

// Re-export types for convenience
export type {
  AlarmChatRoom,
  ChatMessage,
  ChatMessageAttachment,
  ChatMessageList,
  ChatRoom,
  ChatSenderType,
  SendMessageResponse,
  UploadFileResponse,
} from '~/api/model/chatting';

// ============================================================================
// Query 훅
// ============================================================================

/** 전체 읽지 않은 메시지 수 조회 */
// GET /api/market-place/chat/unread-count
export const useGetTotalUnreadCount = () => {
  const { queryKey, queryFn } = CHATTING_QUERY_KEY.totalUnreadCount();
  return useQuery(queryKey, queryFn, {
    suspense: true,
  });
};

/** 알람 채팅방 목록 조회 */
// GET /api/market-place/chat/rooms/alarm
export const useGetAlarmChatRooms = () => {
  const { queryKey, queryFn } = CHATTING_QUERY_KEY.alarmRooms();
  return useQuery(queryKey, queryFn, {
    suspense: true,
  });
};

/** 단일 알람 채팅방 조회 */
// GET /api/market-place/chat/rooms/{roomId}
export const useGetAlarmChatRoom = (roomId: number) => {
  const { queryKey, queryFn } = CHATTING_QUERY_KEY.alarmRoom(roomId);
  return useQuery(queryKey, queryFn, {
    suspense: true,
    enabled: !!roomId,
  });
};

/** 채팅방 푸시 알림 수신 여부 조회 */
// GET /api/market-place/chat/rooms/{roomId}/push-enabled
export const useGetPushEnabled = (roomId: string) => {
  const { queryKey, queryFn } = CHATTING_QUERY_KEY.pushEnabled(roomId);
  return useQuery(queryKey, queryFn, {
    suspense: true,
    enabled: !!roomId,
  });
};

/** 채팅방 푸시 알림 수신 여부 설정 */
// POST /api/market-place/chat/rooms/{roomId}/push-enabled
export const useSetPushEnabled = () => {
  return useMutation(
    ({ roomId, enabled }: { roomId: string; enabled: boolean }) =>
      chattingRepository.setPushEnabled(roomId, enabled),
  );
};

/** 채팅 메시지 목록 조회 */
// GET /api/market-place/chat/rooms/{roomId}/messages
export const useGetMessages = (roomId: string, offset: number) => {
  const { queryKey, queryFn } = CHATTING_QUERY_KEY.messages(roomId, offset);

  return useQuery(queryKey, queryFn, {
    enabled: !!roomId,
    // SSE가 실시간 업데이트를 처리하므로 자동 re-fetch 불필요
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    // React 18 + React Query v3 concurrent rendering 호환성 이슈로 suspense 비활성화
    // (suspense: true 시 React 18이 상태 업데이트를 취소해 queryFn 무한 호출 발생)
    suspense: false,
  });
};

// ============================================================================
// Mutation 훅
// ============================================================================

/** 메시지 전송 */
// POST /api/market-place/chat/messages
export const useSendMessage = () => {
  return useMutation((body: SendMessageBody) =>
    chattingRepository.sendMessage(body),
  );
};

/** 파일 업로드 (presigned URL 획득) */
// POST /api/market-place/chat/files/upload
export const useUploadFile = () => {
  return useMutation((body: UploadFileBody) =>
    chattingRepository.uploadFile(body),
  );
};

/** 채팅방 생성 */
// POST /api/market-place/chat/rooms
export const useCreateChatRoom = () => {
  return useMutation((body: CreateRoomBody) =>
    chattingRepository.createRoom(body),
  );
};
